import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { AcquisitionChain } from '../components/AcquisitionChain';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reveal animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const Home = () => {
  return (
    <div style={{ background: 'var(--ink)' }}>
      {/* ================= HERO ================= */}
      <section
        className="relative overflow-hidden"
        style={{ padding: '120px 0 100px' }}
        data-testid="hero-section"
      >
        {/* Glows */}
        <div
          className="glow"
          style={{
            width: 780, height: 780, left: '50%', top: -360,
            transform: 'translateX(-50%)',
            background: 'radial-gradient(circle, rgba(70,110,230,0.45), transparent 65%)',
            animation: 'drift1 25s ease-in-out infinite',
          }}
        />
        <div
          className="glow"
          style={{
            width: 520, height: 520, right: -160, top: 260,
            background: 'radial-gradient(circle, rgba(233,180,76,0.18), transparent 65%)',
            animation: 'drift2 30s ease-in-out infinite',
          }}
        />
        <div className="grid-bg" />

        <div className="relative max-w-[1160px] mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <motion.span
              className="eyebrow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              data-testid="hero-eyebrow"
            >
              Customer Acquisition Systems
            </motion.span>

            <h1
              style={{
                fontFamily: 'var(--display)',
                fontWeight: 900,
                letterSpacing: '-0.035em',
                fontSize: 'clamp(38px, 5vw, 68px)',
                lineHeight: 1.02,
                margin: '4px 0 0',
                color: 'var(--text)',
              }}
              data-testid="hero-headline"
            >
              Most businesses stop at the lead.{' '}
              <span
                style={{
                  fontFamily: 'var(--serif)',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  color: 'var(--gold)',
                  letterSpacing: '-0.02em',
                }}
              >
                We build what happens after it.
              </span>
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex gap-3.5 items-stretch mt-6 max-w-[600px]"
            >
              <span
                style={{
                  width: 3,
                  borderRadius: 2,
                  background: 'var(--gold)',
                  flex: 'none',
                }}
              />
              <p
                style={{
                  fontFamily: 'var(--display)',
                  fontWeight: 600,
                  fontSize: 'clamp(17px, 1.6vw, 20px)',
                  lineHeight: 1.35,
                  color: '#fff',
                  letterSpacing: '-0.01em',
                }}
              >
                Solidcore Venture builds customer-acquisition systems.{' '}
                <span style={{ color: 'var(--muted)', fontWeight: 500 }}>
                  Market, offer, positioning, message, ads, funnel, follow-up and sales.
                </span>
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              style={{
                fontSize: 'clamp(17px, 1.5vw, 19.5px)',
                color: 'var(--muted)',
                maxWidth: 640,
                marginTop: 18,
              }}
            >
              For businesses with a real offer and creators with a real audience. We map the full path from attention to customer.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              style={{ marginTop: 40 }}
            >
              <div className="flex flex-wrap gap-3">
                <a href="#contact" className="btn btn-gold btn-lg" data-testid="hero-primary-cta">
                  Start a conversation
                  <span className="arrow">→</span>
                </a>
                <a href="#approach" className="btn btn-outline btn-lg" data-testid="hero-secondary-cta">
                  See how we think
                </a>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm" style={{ color: 'var(--muted)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gold)', fontWeight: 700 }}>✓</span> Acquisition systems
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gold)', fontWeight: 700 }}>✓</span> Creator backends
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--gold)', fontWeight: 700 }}>✓</span> Founder-led
                </span>
              </div>
            </motion.div>
          </div>

          {/* Right: animated acquisition chain */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:flex items-center justify-center h-[600px]"
          >
            <AcquisitionChain />
          </motion.div>
        </div>

        <style>{`
          @keyframes drift1 { 0%, 100% { transform: translate(-50%, 0); } 50% { transform: translate(-45%, 20px); } }
          @keyframes drift2 { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(-30px, -20px); } }
        `}</style>
      </section>

      {/* ================= APPROACH (cream) ================= */}
      <section
        id="approach"
        className="cream relative"
        style={{ background: 'var(--cream)', color: 'var(--char)', padding: '120px 0' }}
        data-testid="approach-section"
      >
        <div className="max-w-[1160px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={stagger}
            style={{ maxWidth: 920 }}
          >
            <motion.span variants={fadeUp} className="eyebrow" style={{ color: '#A36F12' }}>
              <span style={{ background: '#C58A1F', boxShadow: 'none' }} />
              Our Approach
            </motion.span>
            <motion.h2
              variants={fadeUp}
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(38px, 5.6vw, 74px)',
                fontWeight: 900,
                letterSpacing: '-0.035em',
                lineHeight: 0.98,
              }}
            >
              Most businesses stop at{' '}
              <span
                style={{
                  color: 'var(--char-3)',
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                the lead.
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    position: 'absolute',
                    left: '-1%',
                    right: '-1%',
                    top: '55%',
                    height: '0.07em',
                    background: 'var(--red)',
                    transform: 'rotate(-2deg)',
                    transformOrigin: 'left',
                  }}
                />
              </span>{' '}
              <span
                style={{
                  fontFamily: 'var(--serif)',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  color: '#B07A16',
                }}
              >
                We don't.
              </span>
            </motion.h2>
            <motion.p variants={fadeUp} style={{ fontSize: '19px', color: 'var(--char-2)', maxWidth: 700, marginTop: 20 }}>
              Ads are one part of acquisition. The offer, the message, the funnel and everything after the click decide whether traffic turns into revenue.
            </motion.p>
          </motion.div>

          {/* System chain — dark card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            style={{
              marginTop: 64,
              background: 'var(--char)',
              color: 'var(--text)',
              borderRadius: 26,
              padding: '44px 40px 40px',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 50px 100px -50px rgba(20,22,27,0.7)',
            }}
          >
            <div
              className="flex justify-between gap-4 flex-wrap font-mono"
              style={{ fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 26 }}
            >
              <span>Acquisition is one connected system</span>
              <b style={{ color: 'var(--gold)', fontWeight: 500 }}>We plan the whole thing</b>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5">
              {[
                { label: 'MARKET' },
                { label: 'OFFER' },
                { label: 'POSITIONING' },
                { label: 'MESSAGE' },
                { label: 'CREATIVE', tag: 'MOST START HERE' },
                { label: 'ADS', tag: 'AND STOP HERE' },
                { label: 'FUNNEL' },
                { label: 'QUALIFICATION' },
                { label: 'FOLLOW-UP' },
                { label: 'SALES' },
                { label: 'CUSTOMER', end: true },
              ].map((node, i, arr) => (
                <React.Fragment key={node.label}>
                  <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.06 }}
                    style={{
                      fontFamily: 'var(--display)',
                      fontWeight: 700,
                      fontSize: 15,
                      letterSpacing: '0.02em',
                      textTransform: 'uppercase',
                      padding: '11px 16px',
                      borderRadius: 10,
                      border: node.end
                        ? '1px solid var(--gold)'
                        : node.tag
                          ? '1px solid rgba(255,255,255,0.35)'
                          : '1px solid rgba(233,180,76,0.35)',
                      background: node.end
                        ? 'var(--gold)'
                        : node.tag
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(233,180,76,0.07)',
                      color: node.end ? 'var(--gold-ink)' : node.tag ? '#fff' : '#F4E2BA',
                      whiteSpace: 'nowrap',
                      position: 'relative',
                    }}
                  >
                    {node.tag && (
                      <span
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: -11,
                          transform: 'translateX(-50%)',
                          fontFamily: 'var(--mono)',
                          fontSize: 9,
                          letterSpacing: '0.1em',
                          background: '#fff',
                          color: 'var(--char)',
                          padding: '2px 7px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {node.tag}
                      </span>
                    )}
                    {node.label}
                  </motion.span>
                  {i < arr.length - 1 && (
                    <span style={{ color: 'var(--gold)', fontSize: 15, opacity: 0.8 }}>→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div
              className="mt-6 flex items-center gap-3 font-mono"
              style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold)' }}
            >
              <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(233,180,76,0.5))' }} />
              ↺ What happens in sales feeds back into marketing
              <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(233,180,76,0.5), transparent)' }} />
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-8">
              <div style={{ borderRadius: 14, padding: '20px 22px', border: '1px solid var(--line)' }}>
                <b
                  className="font-mono"
                  style={{ display: 'block', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8, fontWeight: 500 }}
                >
                  The usual question
                </b>
                <p style={{ fontSize: 15.5, color: '#cfd5e1' }}>"How much can you spend on ads?"</p>
              </div>
              <div style={{ borderRadius: 14, padding: '20px 22px', border: '1px solid rgba(233,180,76,0.4)', background: 'rgba(233,180,76,0.07)' }}>
                <b
                  className="font-mono"
                  style={{ display: 'block', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 8, fontWeight: 500 }}
                >
                  We ask
                </b>
                <p style={{ fontSize: 15.5, color: '#cfd5e1' }}>
                  "What should you actually put money behind, and what has to happen after someone clicks?"
                </p>
              </div>
            </div>
          </motion.div>

          {/* Worthless leads grid */}
          <div className="grid md:grid-cols-[0.8fr_1.2fr] gap-10 mt-20 items-start">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3
                style={{
                  fontFamily: 'var(--display)',
                  fontSize: 'clamp(28px, 3.2vw, 40px)',
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.02,
                }}
              >
                A cheap lead is{' '}
                <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: '#B07A16', fontWeight: 400 }}>
                  worthless
                </span>{' '}
                if…
              </h3>
              <p style={{ marginTop: 16, color: 'var(--char-2)', fontSize: 16.5 }}>
                Your CPL can look great while your business gets nothing.{' '}
                <b style={{ color: 'var(--char)' }}>Cheap leads aren't the goal. Customers are.</b>
              </p>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
            >
              {[
                "It's the wrong person",
                'The offer is weak',
                'The message creates no demand',
                'The landing page loses them',
                'Nobody qualifies them',
                'Nobody follows up properly',
                'The sales process breaks',
                'Nobody knows which campaign made the sale',
              ].map((text) => (
                <motion.div
                  key={text}
                  variants={{
                    hidden: { opacity: 0, x: -20 },
                    show: { opacity: 1, x: 0, transition: { duration: 0.4 } },
                  }}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    background: 'var(--paper)',
                    border: '1px solid #E2D9C7',
                    borderRadius: 12,
                    padding: '14px 16px',
                    fontSize: 15,
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ color: 'var(--red)', fontWeight: 800, flex: 'none' }}>✕</span>
                  {text}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= TWO PILLARS (dark) ================= */}
      <section className="relative overflow-hidden" style={{ padding: '120px 0' }} data-testid="pillars-section">
        <div
          className="glow"
          style={{
            width: 900, height: 600, left: -300, top: 100,
            background: 'radial-gradient(circle, rgba(70,110,230,0.22), transparent 65%)',
          }}
        />

        <div className="relative max-w-[1160px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-16"
            style={{ maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}
          >
            <motion.span variants={fadeUp} className="eyebrow">
              Two ways in
            </motion.span>
            <motion.h2
              variants={fadeUp}
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(34px, 4.8vw, 60px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.02,
                color: 'var(--text)',
              }}
            >
              Two ways in.{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--gold)', fontWeight: 400 }}>
                One system underneath.
              </span>
            </motion.h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Pillar A: Acquisition */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col"
              style={{
                padding: '36px 32px',
                borderRadius: 20,
                border: '1px solid rgba(233,180,76,0.4)',
                background: 'linear-gradient(180deg, rgba(233,180,76,0.06), rgba(233,180,76,0.01))',
              }}
              data-testid="pillar-acquisition"
            >
              <div
                className="font-mono"
                style={{ fontSize: 12, letterSpacing: '0.14em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 10 }}
              >
                Pillar A · For Businesses
              </div>
              <h3
                style={{
                  fontFamily: 'var(--display)',
                  fontSize: 32,
                  fontWeight: 900,
                  letterSpacing: '-0.025em',
                  color: 'var(--text)',
                  marginBottom: 12,
                }}
              >
                Acquisition Systems
              </h3>
              <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
                For businesses with an existing offer that want the full path to customer built and running.
              </p>
              <ul className="space-y-2.5 flex-1">
                {[
                  'Offer & positioning',
                  'Competitor and market research',
                  'Message and creative',
                  'Paid ads',
                  'Funnel and conversion path',
                  'Qualification and follow-up',
                  'Sales handoff',
                  'Measurement that ties campaigns to customers',
                ].map((item) => (
                  <li key={item} className="flex gap-2.5 items-start text-sm" style={{ color: '#dde3ee' }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 700, flex: 'none' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
                style={{ color: 'var(--gold)' }}
                data-testid="pillar-a-link"
              >
                Talk to us about acquisition <span>→</span>
              </a>
            </motion.div>

            {/* Pillar B: Creator Backend */}
            <motion.div
              id="creator-backend"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex flex-col"
              style={{
                padding: '36px 32px',
                borderRadius: 20,
                border: '1px solid var(--line-2)',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
              }}
              data-testid="pillar-creator"
            >
              <div
                className="font-mono"
                style={{ fontSize: 12, letterSpacing: '0.14em', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 10 }}
              >
                Pillar B · For Creators
              </div>
              <h3
                style={{
                  fontFamily: 'var(--display)',
                  fontSize: 32,
                  fontWeight: 900,
                  letterSpacing: '-0.025em',
                  color: 'var(--text)',
                  marginBottom: 12,
                }}
              >
                Creator Backend
              </h3>
              <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
                For creators with attention. We build the business layer behind the content.
              </p>
              <ul className="space-y-2.5 flex-1">
                {[
                  { title: 'Offer Design', desc: 'The right offer for the audience.' },
                  { title: 'Brand Positioning', desc: 'How the creator is presented in market.' },
                  { title: 'Funnel Structure', desc: 'The path from content to buyer.' },
                  { title: 'Lead Conversion', desc: 'DM flow, qualification, sales direction.' },
                  { title: 'Community Backend', desc: 'Onboarding, retention, value delivery.' },
                  { title: 'Scaling Infrastructure', desc: 'Optimization once the system is live.' },
                ].map((item) => (
                  <li key={item.title} className="flex gap-3 items-start text-sm" style={{ color: '#dde3ee' }}>
                    <span style={{ color: 'var(--gold)', fontWeight: 700, flex: 'none' }}>✓</span>
                    <div>
                      <b style={{ color: 'var(--text)', fontWeight: 600 }}>{item.title}:</b>{' '}
                      <span style={{ color: 'var(--muted)' }}>{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Creator flow chain */}
              <div className="mt-8 pt-6 border-t" style={{ borderColor: 'var(--line)' }}>
                <div
                  className="font-mono mb-3"
                  style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--dim)', textTransform: 'uppercase' }}
                >
                  The Chain
                </div>
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {['Content', 'Attention', 'Offer', 'Funnel', 'Conversion', 'Community', 'Retention', 'Scale'].map((step, i, arr) => (
                    <React.Fragment key={step}>
                      <span
                        style={{
                          padding: '5px 10px',
                          borderRadius: 6,
                          background: 'rgba(233,180,76,0.08)',
                          border: '1px solid rgba(233,180,76,0.22)',
                          color: '#F4E2BA',
                          fontFamily: 'var(--mono)',
                          fontSize: 10,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {step}
                      </span>
                      {i < arr.length - 1 && <span style={{ color: 'var(--gold)', opacity: 0.6 }}>→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <a
                href="#contact"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
                style={{ color: 'var(--gold)' }}
                data-testid="pillar-b-link"
              >
                Talk to us about your backend <span>→</span>
              </a>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center mt-12 text-lg"
            style={{ color: 'var(--muted)', maxWidth: 780, marginLeft: 'auto', marginRight: 'auto' }}
          >
            Good acquisition isn't traffic pointed at a business.{' '}
            <span style={{ color: '#fff' }}>It's a designed path from the right person's attention to a buying decision.</span>
          </motion.p>
        </div>
      </section>

      {/* ================= HOW WE THINK (cream) ================= */}
      <section
        className="cream"
        style={{ background: 'var(--cream)', color: 'var(--char)', padding: '120px 0' }}
        data-testid="thinking-section"
      >
        <div className="max-w-[1160px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ maxWidth: 760, marginBottom: 52 }}
          >
            <span className="eyebrow" style={{ color: '#A36F12' }}>
              How we think
            </span>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(34px, 4.8vw, 60px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 0.98,
              }}
            >
              Research first. Decisions second.{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: '#B07A16', fontWeight: 400 }}>
                Ads third.
              </span>
            </h2>
          </motion.div>

          {/* Not cards */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {[
              'A media-buying agency',
              'A course',
              'A template funnel',
              "Traffic for traffic's sake",
            ].map((title) => (
              <motion.div
                key={title}
                variants={fadeUp}
                style={{
                  border: '1px dashed #C0B49C',
                  background: 'transparent',
                  borderRadius: 14,
                  padding: '24px 22px',
                  position: 'relative',
                }}
              >
                <span
                  className="font-mono"
                  style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--red)', textTransform: 'uppercase' }}
                >
                  Not
                </span>
                <h3
                  style={{
                    fontFamily: 'var(--display)',
                    fontSize: 20,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: 'var(--char)',
                    marginTop: 8,
                    textDecoration: 'line-through',
                    textDecorationColor: 'var(--red)',
                    textDecorationThickness: '1.5px',
                  }}
                >
                  {title}
                </h3>
              </motion.div>
            ))}
          </motion.div>

          {/* It is */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-6 rounded-[18px] p-8 md:p-10"
            style={{ background: 'var(--char)', color: 'var(--text)' }}
          >
            <span
              className="font-mono"
              style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--gold)', textTransform: 'uppercase' }}
            >
              It is
            </span>
            <h3
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(26px, 3vw, 36px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                marginTop: 10,
              }}
            >
              A partner that builds the whole path from{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--gold)', fontWeight: 400 }}>
                attention to customer
              </span>
              , starting with research into your market, competitors and customers.
            </h3>
          </motion.div>

          {/* Books */}
          <div className="mt-20">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              style={{ maxWidth: 760, marginBottom: 40 }}
            >
              <h3
                style={{
                  fontFamily: 'var(--display)',
                  fontSize: 'clamp(28px, 3.6vw, 44px)',
                  fontWeight: 900,
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                }}
              >
                You could read the books.{' '}
                <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: '#B07A16', fontWeight: 400 }}>
                  We apply them to your business.
                </span>
              </h3>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 gap-3"
            >
              {[
                { title: '$100M Offers', author: 'Alex Hormozi', use: 'Offer' },
                { title: '$100M Leads', author: 'Alex Hormozi', use: 'Acquisition' },
                { title: '$100M Money Models', author: 'Alex Hormozi', use: 'Economics' },
                { title: 'Breakthrough Advertising', author: 'Eugene M. Schwartz', use: 'Market + message' },
                { title: 'Positioning: The Battle for Your Mind', author: 'Al Ries & Jack Trout', use: 'Competitive angle' },
                { title: 'Obviously Awesome', author: 'April Dunford', use: 'Differentiation' },
                { title: 'Scientific Advertising', author: 'Claude Hopkins', use: 'Testing' },
                { title: 'Influence', author: 'Robert Cialdini', use: 'Decision psychology' },
              ].map((book) => (
                <motion.div
                  key={book.title}
                  variants={fadeUp}
                  className="flex items-center justify-between gap-4 p-5"
                  style={{
                    background: 'var(--paper)',
                    borderRadius: 12,
                    border: '1px solid #E2D9C7',
                  }}
                >
                  <div>
                    <b
                      style={{
                        fontFamily: 'var(--display)',
                        fontWeight: 700,
                        fontSize: 16,
                        letterSpacing: '-0.01em',
                        color: 'var(--char)',
                        display: 'block',
                      }}
                    >
                      {book.title}
                    </b>
                    <span style={{ color: 'var(--char-2)', fontSize: 13 }}>{book.author}</span>
                  </div>
                  <div
                    className="font-mono flex-none"
                    style={{
                      color: '#9A6B12',
                      background: '#FBF1DA',
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 10.5,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    → {book.use}
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <p style={{ marginTop: 28, fontSize: 12.5, color: 'var(--char-2)', fontStyle: 'italic', maxWidth: 720 }}>
              Book titles are listed only to show the ideas behind our process. We have no affiliation with, and no endorsement from, their authors or publishers.
            </p>
          </div>
        </div>
      </section>

      {/* ================= FOUNDER (dark) ================= */}
      <section
        id="founder"
        className="relative overflow-hidden"
        style={{ padding: '120px 0' }}
        data-testid="founder-section"
      >
        <div
          className="glow"
          style={{
            width: 600, height: 600, right: -200, top: -100,
            background: 'radial-gradient(circle, rgba(70,110,230,0.15), transparent 65%)',
          }}
        />

        <div className="relative max-w-[1160px] mx-auto px-6 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-center">
          {/* Founder Card */}
          <motion.div
            initial={{ opacity: 0, x: -30, rotate: -3 }}
            whileInView={{ opacity: 1, x: 0, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative mx-auto lg:mx-0 w-full"
            style={{ maxWidth: 340 }}
          >
            {/* Offset gold frame */}
            <div
              aria-hidden="true"
              className="absolute rounded-[24px]"
              style={{
                inset: 0,
                transform: 'translate(14px, 14px)',
                border: '1px solid rgba(233,180,76,0.45)',
                pointerEvents: 'none',
              }}
            />
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="relative rounded-[24px] overflow-hidden"
              style={{
                boxShadow: '0 50px 100px -30px rgba(0,0,0,0.8), 0 0 0 1px rgba(233,180,76,0.25)',
                transform: 'rotate(-2deg)',
              }}
            >
              <img
                src="/founder/founder-photo.jpg"
                alt="Kushagra Kaushik, Founder of Solidcore Venture"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="founder-image"
              />
            </motion.div>
          </motion.div>

          {/* Founder Bio */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="eyebrow">Founder</span>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(36px, 4.6vw, 58px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.02,
                color: 'var(--text)',
                marginBottom: 8,
              }}
            >
              Kushagra Kaushik
            </h2>
            <p style={{ color: 'var(--muted)', fontSize: 18, marginBottom: 28 }}>
              Founder, Solidcore Venture
            </p>

            <p style={{ color: 'var(--muted)', fontSize: 17, lineHeight: 1.7, marginBottom: 32 }}>
              I don't try to be a better media buyer than every other agency. I think more broadly about the problem: the market, the customer, the competitors and the offer first, then the message, the creative and the funnel, and finally whether attention is actually turning into customers and revenue.
            </p>

            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:kuzagra@solidcoreventure.com"
                className="btn btn-outline"
                style={{ padding: '16px 22px', fontSize: 15 }}
                data-testid="founder-email-cta"
              >
                kuzagra@solidcoreventure.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= WHO IT'S FOR (cream) ================= */}
      <section
        className="cream"
        style={{ background: 'var(--cream)', color: 'var(--char)', padding: '120px 0' }}
        data-testid="fit-section"
      >
        <div className="max-w-[1160px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
            style={{ maxWidth: 760, marginLeft: 'auto', marginRight: 'auto' }}
          >
            <span className="eyebrow" style={{ color: '#A36F12' }}>
              Who it's for
            </span>
            <h2
              style={{
                fontFamily: 'var(--display)',
                fontSize: 'clamp(34px, 4.8vw, 60px)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 0.98,
              }}
            >
              Built for businesses with{' '}
              <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: '#B07A16', fontWeight: 400 }}>
                something worth growing.
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-8 rounded-[18px]"
              style={{
                background: 'var(--paper)',
                border: '1px solid #E2D9C7',
              }}
            >
              <div
                className="font-mono mb-5"
                style={{ fontSize: 11, letterSpacing: '0.14em', color: '#B07A16', textTransform: 'uppercase' }}
              >
                For you if
              </div>
              <ul className="space-y-3">
                {[
                  'Businesses with customers and an offer',
                  "You've run ads and don't know what to test next",
                  'You have several offers and need to know which one to advertise',
                  "You're getting leads but not enough customers",
                  'Creators with attention but no clean offer or backend',
                ].map((item) => (
                  <li key={item} className="flex gap-3 items-start" style={{ fontSize: 15.5 }}>
                    <span style={{ color: '#B07A16', fontWeight: 700, flex: 'none' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 rounded-[18px]"
              style={{
                background: 'var(--char)',
                color: 'var(--text)',
              }}
            >
              <div
                className="font-mono mb-5"
                style={{ fontSize: 11, letterSpacing: '0.14em', color: 'var(--gold)', textTransform: 'uppercase' }}
              >
                Not for you if
              </div>
              <ul className="space-y-3" style={{ color: 'var(--muted)' }}>
                {[
                  "You've never sold your offer",
                  "You're still looking for a business idea",
                  'You want guaranteed revenue',
                ].map((item) => (
                  <li key={item} className="flex gap-3 items-start" style={{ fontSize: 15.5 }}>
                    <span style={{ color: 'var(--red)', fontWeight: 700, flex: 'none' }}>✕</span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA (dark, strong glow) ================= */}
      <section
        className="relative overflow-hidden"
        style={{ padding: '140px 0' }}
        data-testid="final-cta-section"
      >
        <div
          className="glow"
          style={{
            width: 900, height: 900, left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(233,180,76,0.28), transparent 65%)',
          }}
        />
        <div
          className="glow"
          style={{
            width: 600, height: 600, left: -100, top: -100,
            background: 'radial-gradient(circle, rgba(70,110,230,0.2), transparent 65%)',
          }}
        />

        <div className="relative max-w-[900px] mx-auto px-6 text-center">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="eyebrow"
          >
            The strategy starts before the campaign
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            style={{
              fontFamily: 'var(--display)',
              fontSize: 'clamp(40px, 5.6vw, 76px)',
              fontWeight: 900,
              letterSpacing: '-0.035em',
              lineHeight: 0.98,
              color: 'var(--text)',
              margin: '10px 0 24px',
            }}
          >
            Give us the business.{' '}
            <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--gold)', fontWeight: 400 }}>
              We'll map the path to customers.
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ color: 'var(--muted)', fontSize: 20, maxWidth: 640, margin: '0 auto 40px' }}
          >
            Tell us about your business. We'll tell you where we'd start.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-3 justify-center"
          >
            <a
              href="#contact"
              className="btn btn-gold btn-lg"
              data-testid="final-cta-primary"
            >
              Start a conversation
              <span className="arrow">→</span>
            </a>
            <a
              href="mailto:kuzagra@solidcoreventure.com"
              className="btn btn-outline btn-lg"
              data-testid="final-cta-secondary"
            >
              kuzagra@solidcoreventure.com
            </a>
          </motion.div>
        </div>
      </section>

      {/* ================= CONTACT (dark) ================= */}
      <ContactSection />
    </div>
  );
};

// ================= CONTACT SECTION =================
const ContactSection = () => {
  const [form, setForm] = useState({ name: '', email: '', website: '', profile: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validEmail(form.email)) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
      setForm({ name: '', email: '', website: '', profile: '', message: '' });
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again, or email kuzagra@solidcoreventure.com directly.');
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--line-2)',
    borderRadius: 12,
    padding: '14px 16px',
    color: 'var(--text)',
    fontSize: 15.5,
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'var(--body)',
  };

  const labelStyle = {
    display: 'block',
    fontFamily: 'var(--mono)',
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    marginBottom: 8,
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden border-t"
      style={{ padding: '120px 0', borderColor: 'var(--line)', background: 'var(--night)' }}
      data-testid="contact-section"
    >
      <div
        className="glow"
        style={{
          width: 700, height: 700, left: '50%', bottom: -400,
          transform: 'translateX(-50%)',
          background: 'radial-gradient(circle, rgba(70,110,230,0.22), transparent 65%)',
        }}
      />

      <div className="relative max-w-[1160px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14"
        >
          <span className="eyebrow">Contact</span>
          <h2
            style={{
              fontFamily: 'var(--display)',
              fontSize: 'clamp(34px, 4.6vw, 58px)',
              fontWeight: 900,
              letterSpacing: '-0.03em',
              lineHeight: 1.02,
              color: 'var(--text)',
              maxWidth: 760,
            }}
          >
            Tell us what you're building.{' '}
            <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--gold)', fontWeight: 400 }}>
              We'll show you where we'd start.
            </span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
          {/* Left: direct contact */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="space-y-8">
              <div>
                <div className="font-mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--dim)', marginBottom: 6 }}>
                  Email
                </div>
                <a
                  href="mailto:kuzagra@solidcoreventure.com"
                  className="hover:text-white transition-colors"
                  style={{ color: 'var(--text)', fontSize: 19, fontWeight: 600, fontFamily: 'var(--display)' }}
                  data-testid="contact-email-link"
                >
                  kuzagra@solidcoreventure.com
                </a>
              </div>
              <div
                className="pt-6 border-t"
                style={{ borderColor: 'var(--line)', color: 'var(--muted)', fontSize: 15.5 }}
              >
                Every message is read by the founder.
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="rounded-[20px] p-7 md:p-9"
            style={{
              border: '1px solid var(--line-2)',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
            }}
            data-testid="contact-form-card"
          >
            {status === 'success' ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div
                  className="w-14 h-14 rounded-full mx-auto mb-5 grid place-items-center"
                  style={{ background: 'rgba(233,180,76,0.12)', border: '1px solid rgba(233,180,76,0.4)' }}
                >
                  <span style={{ color: 'var(--gold)', fontSize: 24, fontWeight: 700 }}>✓</span>
                </div>
                <h3
                  style={{
                    fontFamily: 'var(--display)',
                    fontSize: 24,
                    fontWeight: 800,
                    color: 'var(--text)',
                    marginBottom: 8,
                  }}
                >
                  Got it.
                </h3>
                <p style={{ color: 'var(--muted)', fontSize: 15.5 }}>
                  We'll reply from <b style={{ color: 'var(--text)' }}>kuzagra@solidcoreventure.com</b>.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-sm font-semibold transition-colors"
                  style={{ color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer' }}
                  data-testid="contact-send-another"
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="cf-name" style={labelStyle}>Name *</label>
                    <input
                      id="cf-name"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="Your name"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" style={labelStyle}>Email *</label>
                    <input
                      id="cf-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="you@company.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="cf-website" style={labelStyle}>Website or social link</label>
                  <input
                    id="cf-website"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    style={inputStyle}
                    placeholder="yoursite.com"
                    data-testid="contact-website-input"
                  />
                </div>

                <div>
                  <label htmlFor="cf-profile" style={labelStyle}>I'm a…</label>
                  <select
                    id="cf-profile"
                    name="profile"
                    value={form.profile}
                    onChange={handleChange}
                    style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
                    data-testid="contact-profile-select"
                  >
                    <option value="" style={{ background: '#0A0F1C' }}>Select one</option>
                    <option value="business" style={{ background: '#0A0F1C' }}>Business with an offer</option>
                    <option value="creator" style={{ background: '#0A0F1C' }}>Creator with an audience</option>
                    <option value="other" style={{ background: '#0A0F1C' }}>Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="cf-message" style={labelStyle}>What do you need help with? *</label>
                  <textarea
                    id="cf-message"
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }}
                    placeholder="A sentence or two about your business and where you're stuck."
                    data-testid="contact-message-textarea"
                  />
                </div>

                {status === 'error' && (
                  <div
                    className="rounded-xl px-4 py-3 text-sm"
                    style={{ background: 'rgba(201,84,62,0.1)', border: '1px solid rgba(201,84,62,0.35)', color: '#E8927F' }}
                    data-testid="contact-error"
                  >
                    {errorMsg}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-gold w-full"
                  disabled={status === 'sending'}
                  style={{ opacity: status === 'sending' ? 0.7 : 1 }}
                  data-testid="contact-submit-button"
                >
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                  <span className="arrow">→</span>
                </button>

                <p
                  className="text-center"
                  style={{ fontSize: 12.5, color: 'var(--dim)', lineHeight: 1.6 }}
                  data-testid="contact-privacy-notice"
                >
                  By submitting this form, you acknowledge that your information will be used to respond to your enquiry in accordance with our{' '}
                  <a
                    href="/privacy-policy"
                    className="underline underline-offset-2 transition-colors hover:text-white"
                    style={{ color: 'var(--gold-2)' }}
                    data-testid="contact-privacy-link"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Home;
